import { http } from "./http";
import { listSchools } from "./platformService";

/** Un résultat de la recherche de l'en-tête : un clic ouvre `link`. */
export interface SearchResult {
  key: string;
  icon: string;
  label: string;
  detail: string;
  link: string;
}

export interface SearchGroup {
  title: string;
  results: SearchResult[];
}

const LIMIT = 5;

interface Person {
  id: number;
  firstName: string;
  lastName: string;
}

const fullName = (p: Person) => `${p.firstName} ${p.lastName}`;
const withQuery = (path: string, q: string) => `${path}?q=${encodeURIComponent(q)}`;

/** Premiers résultats d'une liste du serveur (recherche déjà faite côté serveur, cloisonnée par école et cycle). */
async function firstMatches<T>(path: string, search: string): Promise<T[]> {
  return http.get<T[]>(path, { search, limit: LIMIT, page: 1 });
}

/**
 * Recherche globale : élèves, parents, classes, enseignants, personnel et utilisateurs, chacun seulement
 * si l'utilisateur a le droit de les voir ; les écoles pour le super-administrateur.
 */
export async function globalSearch(
  term: string,
  can: (permission: string) => boolean,
  isPlatformAdmin: boolean,
): Promise<SearchGroup[]> {
  const q = term.trim();
  if (q.length < 2) return [];

  const tasks: Array<Promise<SearchGroup>> = [];

  if (isPlatformAdmin) {
    tasks.push(
      listSchools().then((schools) => {
        const needle = q.toLowerCase();
        return {
          title: "Écoles",
          results: schools
            .filter((s) => [s.name, s.shortName, s.code, s.city].some((v) => v?.toLowerCase().includes(needle)))
            .slice(0, LIMIT)
            .map((s) => ({
              key: `school-${s.id}`,
              icon: "ri-building-2-line",
              label: s.name,
              detail: [s.code, s.city].filter(Boolean).join(" · "),
              link: withQuery("/dashboard/platform", s.name),
            })),
        };
      }),
    );
  }

  if (can("students:read")) {
    tasks.push(
      firstMatches<Person & { matricule: string }>("/students", q).then((rows) => ({
        title: "Élèves",
        results: rows.map((s) => ({
          key: `student-${s.id}`,
          icon: "ri-user-3-line",
          label: fullName(s),
          detail: `Matricule ${s.matricule}`,
          link: `${withQuery("/dashboard/students", s.matricule)}&open=${s.id}`,
        })),
      })),
    );
  }

  if (can("guardians:read")) {
    tasks.push(
      firstMatches<Person & { phone: string }>("/guardians", q).then((rows) => ({
        title: "Parents",
        results: rows.map((g) => ({
          key: `guardian-${g.id}`,
          icon: "ri-parent-line",
          label: fullName(g),
          detail: g.phone,
          link: withQuery("/dashboard/guardians", g.phone || fullName(g)),
        })),
      })),
    );
  }

  if (can("classes:read")) {
    tasks.push(
      firstMatches<{ id: number; name: string; capacity: number | null }>("/classes", q).then((rows) => ({
        title: "Classes",
        results: rows.map((c) => ({
          key: `class-${c.id}`,
          icon: "ri-door-open-line",
          label: c.name,
          detail: c.capacity ? `${c.capacity} places` : "Classe",
          link: withQuery("/dashboard/classes", c.name),
        })),
      })),
    );
  }

  if (can("teachers:read")) {
    tasks.push(
      firstMatches<Person & { phone: string | null }>("/teachers", q).then((rows) => ({
        title: "Enseignants",
        results: rows.map((t) => ({
          key: `teacher-${t.id}`,
          icon: "ri-presentation-line",
          label: fullName(t),
          detail: t.phone ?? "Enseignant",
          link: withQuery("/dashboard/teachers", fullName(t)),
        })),
      })),
    );
  }

  if (can("staff:read")) {
    tasks.push(
      firstMatches<Person & { phone: string | null }>("/staff", q).then((rows) => ({
        title: "Personnel",
        results: rows.map((s) => ({
          key: `staff-${s.id}`,
          icon: "ri-team-line",
          label: fullName(s),
          detail: s.phone ?? "Personnel",
          link: withQuery("/dashboard/staff", fullName(s)),
        })),
      })),
    );
  }

  if (can("users:read")) {
    tasks.push(
      firstMatches<Person & { email: string }>("/users", q).then((rows) => ({
        title: "Utilisateurs",
        results: rows.map((u) => ({
          key: `user-${u.id}`,
          icon: "ri-shield-user-line",
          label: fullName(u),
          detail: u.email,
          link: withQuery("/dashboard/users", u.email),
        })),
      })),
    );
  }

  // Une liste en échec (droit retiré entre-temps…) n'empêche pas d'afficher les autres.
  const settled = await Promise.allSettled(tasks);
  return settled
    .flatMap((r) => (r.status === "fulfilled" ? [r.value] : []))
    .filter((group) => group.results.length > 0);
}
